import React, { type PropsWithChildren, type ReactNode } from 'react';
import { styled as styledNew } from '@compiled/react';
import { Grid, GridColumn } from '@atlaskit/page';
import { token } from '@atlaskit/tokens';
import {
	BreadcrumbManager,
	BreadcrumbRenderer,
	ProjectBreadcrumb,
	ProjectSettingsBreadcrumb,
} from '@atlassian/jira-breadcrumbs/src';
import { ff } from '@atlassian/jira-feature-flagging';
import { ProjectContextProvider } from '@atlassian/jira-providers-project-context';

type Props = {
	children: ReactNode;
	baseUrl: string;
	projectKey: string;
	gridLayout?: 'fixed' | 'fluid';
	fullSize?: boolean;
};

const AppContainer = ({ children, baseUrl, projectKey, gridLayout, fullSize = false }: Props) => (
	<ProjectContextProvider>
		{({ data: projectData }) => (
			<ConditionalGrid gridLayout={gridLayout} fullSize={fullSize}>
				<BreadcrumbManager>
					<BreadcrumbsWrapper>
						<BreadcrumbRenderer />
					</BreadcrumbsWrapper>
					<ProjectBreadcrumb
						baseUrl={baseUrl}
						projectKey={projectKey}
						projectName={projectData ? projectData.projectName : ''}
					>
						<ProjectSettingsBreadcrumb
							baseUrl={baseUrl}
							projectKey={projectKey}
							projectType={projectData ? projectData.projectType : undefined}
							isSimplified={projectData ? projectData.simplified : false}
						>
							{children}
						</ProjectSettingsBreadcrumb>
					</ProjectBreadcrumb>
				</BreadcrumbManager>
			</ConditionalGrid>
		)}
	</ProjectContextProvider>
);
export default AppContainer;

const ConditionalGrid = ({
	children,
	gridLayout,
	fullSize,
}: PropsWithChildren<Pick<Props, 'gridLayout' | 'fullSize'>>) => {
	if (
		fullSize &&
		ff(
			'com.atlassian.connect.change-jira-appcontainer-to-better-support-jira-project-admin-tab-panel',
		)
	) {
		return <FullHeightWrapper>{children}</FullHeightWrapper>;
	}
	return (
		<NormalWrapper>
			<Grid layout={gridLayout}>
				<GridColumn>{children}</GridColumn>
			</Grid>
		</NormalWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NormalWrapper = styledNew.div<{
	children?: ReactNode;
}>({
	flex: '1 1 auto',
	position: 'relative',
	padding: `${token('space.0', '0px')} ${token('space.300', '24px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullHeightWrapper = styledNew(NormalWrapper)({
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300', '24px'),
});

/*
 * The breadcrumbs container height must be min-height and not fixed because
 * some breadcrumbs have user-generated content in it (e. g. next-gen project settings)
 * and it is possible that it will wrap to new line.
 * In that case, let's allow the content to move down, and not overlap.
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BreadcrumbsWrapper = styledNew.div({
	marginTop: token('space.300', '24px'),
	marginBottom: token('space.negative.200', '-16px'),
	marginLeft: token('space.negative.050', '-4px'),
	minHeight: token('space.200', '16px'),
});
